import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Pagination,
  Tabs,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  type ControlledDrugRegisterEntry,
  type ControlledDrugRegisterKind,
  type PoisonBookEntry,
  useControlledDrugRegister,
  usePoisonBook,
} from '@/features/registers/api';
import { PageContainer } from '@/layouts/PageContainer';
import { dayLabel } from '@/pages/ops/register-format';
import { CorrectEntryDialog } from './CorrectEntryDialog';
import styles from './ControlledDrugsPage.module.css';

const REGISTER_TABS: {
  value: ControlledDrugRegisterKind;
  label: 'controlledDrugsPoison' | 'controlledDrugsControlled';
}[] = [
  { value: 'controlled', label: 'controlledDrugsControlled' },
  { value: 'poison', label: 'controlledDrugsPoison' },
];

const dash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '-' : value;

const twoLines = (first: string | null, second: string | null) => (
  <>
    <p>{dash(first)}</p>
    {second && <p className="mono">{second}</p>}
  </>
);

/**
 * The statutory registers recorded by the dispensing API. RL-1635: the
 * controlled-drug register (decision D18) shows every column the API keeps:
 * entry number, date, patient, prescriber, in, out, the running balance and
 * the witness. A mistake is corrected with a new linked entry, never an edit.
 */
export function ControlledDrugsPage() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const canCorrect = hasPermission('dispensary:dispense');
  const [kind, setKind] = useState<ControlledDrugRegisterKind>('controlled');
  const [page, setPage] = useState(1);
  const register = useControlledDrugRegister(kind === 'controlled', page);
  // RL-1634 review: medicines whose register balance and stock differ, or
  // controlled stock not in the register yet: flagged, never absorbed.
  const flagged = (register.data?.medicines ?? []).filter(
    (m) => m.discrepancy !== 0 || !m.inRegister,
  );
  const book = usePoisonBook(kind === 'poison');
  const [correcting, setCorrecting] =
    useState<ControlledDrugRegisterEntry | null>(null);

  const cdColumns: Column<ControlledDrugRegisterEntry>[] = [
    {
      key: 'entry',
      header: t('dispensary.controlledDrugsNumber'),
      render: (e) => <span className="mono">{e.entryNo}</span>,
    },
    {
      key: 'date',
      header: t('dispensary.controlledDrugsDate'),
      render: (e) => dayLabel(e.suppliedAt),
    },
    {
      key: 'medicine',
      header: t('dispensary.controlledDrugsMedicine'),
      render: (e) => (
        <>
          <strong>{e.medicine.name}</strong>
          <p className={styles.sub}>
            {t(`dispensary.cdKind.${e.kind}`)}
            {e.batch ? ` · ${e.batch}` : ''}
            {e.rxNumber ? ` · ${e.rxNumber}` : ''}
          </p>
          {e.corrects && (
            <p className={styles.sub}>
              {t('dispensary.cdCorrects', {
                entry: e.corrects.entryNo,
                reason: e.reason ?? '',
              })}
            </p>
          )}
          {e.correctedBy.length > 0 && (
            <Badge tone="warning">
              {t('dispensary.cdCorrectedBy', {
                entries: e.correctedBy.map((c) => c.entryNo).join(', '),
              })}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'patient',
      header: t('dispensary.controlledDrugsPatient'),
      render: (e) => (
        <>
          {twoLines(e.patientName, e.patientIdentity)}
          {e.collector && (
            <p className={styles.sub}>
              {e.collector === 'patient'
                ? t('dispensary.cdCollectedByPatient', {
                    last4: e.collectorIdLast4 ?? '-',
                  })
                : t('dispensary.cdCollectedBy', {
                    name: e.collectorName ?? '-',
                    last4: e.collectorIdLast4 ?? '-',
                  })}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'prescriber',
      header: t('dispensary.controlledDrugsPrescriber'),
      render: (e) =>
        twoLines(
          e.prescriberName,
          e.prescriberMmc ? `MMC ${e.prescriberMmc}` : null,
        ),
    },
    {
      key: 'in',
      header: t('dispensary.controlledDrugsIn'),
      align: 'right',
      render: (e) => (e.qtyIn ? e.qtyIn : '-'),
    },
    {
      key: 'out',
      header: t('dispensary.controlledDrugsOut'),
      align: 'right',
      render: (e) => (e.qtyOut ? e.qtyOut : '-'),
    },
    {
      key: 'balance',
      header: t('dispensary.controlledDrugsBalance'),
      align: 'right',
      render: (e) => <strong>{e.balanceAfter}</strong>,
    },
    {
      key: 'witness',
      header: t('dispensary.controlledDrugsWitness'),
      render: (e) => dash(e.witnessName),
    },
    ...(canCorrect
      ? [
          {
            key: 'actions',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right' as const,
            render: (e: ControlledDrugRegisterEntry) =>
              // RL-1634 review: openings, counts and corrections are not corrected.
              ['correction', 'opening', 'count_adjust'].includes(
                e.kind,
              ) ? null : (
                <Button size="sm" onClick={() => setCorrecting(e)}>
                  {t('dispensary.cdCorrect')}
                </Button>
              ),
          },
        ]
      : []),
  ];

  const bookColumns: Column<PoisonBookEntry>[] = [
    {
      key: 'date',
      header: t('dispensary.controlledDrugsDate'),
      render: (e) => dayLabel(e.at),
    },
    {
      key: 'rx',
      header: 'Rx',
      render: (e) => <span className="mono">{dash(e.rxNumber)}</span>,
    },
    {
      key: 'medicine',
      header: t('dispensary.controlledDrugsMedicine'),
      render: (e) => (
        <>
          <strong>{e.medicine}</strong>
          {e.group && (
            <p className={styles.sub}>
              {t('dispensary.poisonGroup', { group: e.group })}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'patient',
      header: t('dispensary.controlledDrugsPatient'),
      render: (e) => twoLines(e.patient, e.identity),
    },
    {
      key: 'prescriber',
      header: t('dispensary.controlledDrugsPrescriber'),
      render: (e) => twoLines(e.prescriber, e.mmc ? `MMC ${e.mmc}` : null),
    },
    {
      key: 'quantity',
      header: t('dispensary.controlledDrugsQuantity'),
      align: 'right',
      render: (e) => e.qty,
    },
  ];

  if (!branch)
    return (
      <PageContainer>
        <p>{t('dispensary.controlledDrugsChooseBranch')}</p>
      </PageContainer>
    );

  const tab = REGISTER_TABS.find((item) => item.value === kind)!;
  return (
    <PageContainer>
      <Card
        title={`${t('dispensary.controlledDrugsTitle')} · ${branch.name}`}
        flush
      >
        <Tabs
          label={t('dispensary.controlledDrugsTitle')}
          items={REGISTER_TABS.map((item) => ({
            value: item.value,
            label: t(`dispensary.${item.label}`),
          }))}
          value={kind}
          onChange={setKind}
        />
        {kind === 'controlled' && flagged.length > 0 && (
          <div className={styles.flags} role="status">
            {flagged.map((m) => (
              <p key={m.medicine.id}>
                <Badge tone="danger">
                  {t('dispensary.cdDiscrepancyBadge')}
                </Badge>{' '}
                {m.inRegister
                  ? t('dispensary.cdDiscrepancy', {
                      medicine: m.medicine.name,
                      balance: m.registerBalance ?? 0,
                      onHand: m.onHand,
                    })
                  : t('dispensary.cdNotInRegister', {
                      medicine: m.medicine.name,
                      onHand: m.onHand,
                    })}
              </p>
            ))}
          </div>
        )}
        {kind === 'controlled' ? (
          <DataTable
            caption={t(`dispensary.${tab.label}`)}
            columns={cdColumns}
            rows={register.data?.entries}
            rowKey={(e) => e.id}
            loading={register.isLoading}
            error={register.error}
            onRetry={() => void register.refetch()}
            empty={
              <EmptyState title={t('dispensary.controlledDrugsNoControlled')} />
            }
          />
        ) : (
          <DataTable
            caption={t(`dispensary.${tab.label}`)}
            columns={bookColumns}
            rows={book.data}
            rowKey={(e) => e.id}
            loading={book.isLoading}
            error={book.error}
            onRetry={() => void book.refetch()}
            empty={
              <EmptyState title={t('dispensary.controlledDrugsNoPoison')} />
            }
          />
        )}
      </Card>
      {kind === 'controlled' && register.data?.meta && (
        <Pagination meta={register.data.meta} onPageChange={setPage} />
      )}
      {kind === 'controlled' && (
        <p className={`${styles.sub} ${styles.note}`}>
          {t('dispensary.cdNever')}
        </p>
      )}
      {correcting && (
        <CorrectEntryDialog
          entry={correcting}
          onClose={() => setCorrecting(null)}
        />
      )}
    </PageContainer>
  );
}
